import React, { useEffect, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import { last, difference, capitalize, toLower, uniqueId, startCase, camelCase } from 'lodash';
import { ui, uiA, uiIconSvg, uiButton, uiMerge } from 'envy-ui';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery, useMutation } from '@apollo/client';
// import Countdown, { calcTimeDelta } from 'react-countdown';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { GENERAL_SETTINGS, LAST_SYNC_DATE, SYNC_PAGE_DATA, SET_CLIENT_STATE } from '../../graphqls';
import { Dropdown } from './inputControls';
import { moment as momentConfig } from '../../constants.json';
import getScheduledInfo from '../../util/getScheduledInfo';

export default function MainHeader({ stopPolling, startPolling }) {
  const [withError, setWithError] = useState(false);
  const history = useHistory();
  const { pathname } = useLocation();
  const {
    loading: loadingGeneralSettings,
    data: {
      generalSettings: {
        syncStartOn,
        interfaceAddress = '',
        accountEmail,
        subDomain,
        syncStatus,
        publishedAt,
        newerUpdatesAvailable,
        syncRequestedAt,
        planName,
        billable,
        syncSettings: {
          autoSync = false,
          frequency,
          amPmGenerated,
          timeGenerated,
          weeklyEvery,
          weeklyOn,
          monthlyMode,
          monthlyEvery,
          monthlyEachDates,
          monthlyOnTheDayOption,
          monthlyOnTheFirstLastOption,
          reportingDateBeforeAfter,
          reportingDateDays,
        } = {},
        timeZone: defaultTimeZone,
        reportingDates = '',
      } = {},
    } = {},
  } = useQuery(GENERAL_SETTINGS, {
    onCompleted({ generalSettings: { syncStatus: syncing } }) {
      if (syncing) {
        startPolling(3000);
      }
    },
  });
  const { loading: loadingLastSyncDate, data: { lastSyncDate: { createdAt, appliedAt } = {} } = {} } = useQuery(LAST_SYNC_DATE, {
    onCompleted: ({ lastSyncDate: { createdAt, receivedAt } }) => {
      if (!receivedAt && syncStatus && moment.duration(moment().diff(moment(createdAt))).asDays() >= 1) {
        setWithError(true);
      }
    },
  });
  const { loading: loadingSyncPageData, data: { syncPageData = [] } = {} } = useQuery(SYNC_PAGE_DATA);
  const [setState] = useMutation(SET_CLIENT_STATE);
  const lastPublishDate = publishedAt ? moment(publishedAt).format('MMM D, YYYY, h:mm:ss a') : 'Never';
  const newerUpdatesLabel = newerUpdatesAvailable ? 'There are unpublished changes' : '';
  const lastSyncLabel = syncStatus ? moment(syncRequestedAt).format('MMM D, YYYY, h:mm:ss a') : '';
  const syncClass = syncStatus ? ' disabled' : '';

  useEffect(() => {
    const { stage } = window;

    if (['staging', 'production'].includes(stage) && window.userpilot && billable) {
      const env = last(interfaceAddress.split('.')) === 'com' ? stage : 'productionca';

      window.userpilot.identify(`${env}-pd-${window.userId}`, { email: accountEmail, subdomain: subDomain });
    }
  }, [interfaceAddress]);

  useEffect(() => {
    if (!syncStatus) {
      stopPolling();
    }
  }, [syncStatus]);

  const onToggleGeneralSettings = () => setState({ variables: { sideMenu: 'generalSettingsView' } });
  const onTogglePreviewPublishSettings = () => setState({ variables: { sideMenu: 'PreviewPublishDropdown' } });
  const onSync = () => {
    if (!syncStatus && syncPageData.length) {
      history.push('/sync');
    } else if (!syncStatus || withError) {
      setState({ variables: { modalTypeShown: 'requestSync' } });
    }
  };

  const autosyncStatus = {
    on: { tooltip: 'Autosync is ON', iconModifier: '--autosync-on' },
    off: { tooltip: 'Autosync is OFF', iconModifier: '--autosync-off' },
    error: { tooltip: 'Autosync Error', iconModifier: '--autosync-error' },
  };
  let syncButtonLabel = 'Request Sync';

  if (!withError) {
    if (syncStatus) {
      syncButtonLabel = 'Sync in Progress...';
    } else if (syncPageData.length) {
      syncButtonLabel = 'View Synced Data';
    }
  }

  if (loadingGeneralSettings || loadingLastSyncDate || loadingSyncPageData) {
    return '';
  }

  // let autoSyncInfo = 'Next sync runs on ';
  // let nextMoment;
  let nextMoment = moment();
  let humanizedString = '';
  // let scheduleInfo = '';
  ({ nextMoment, humanizedString } = getScheduledInfo({
    startOn: syncStartOn,
    amPmGenerated,
    frequency,
    monthlyMode,
    monthlyEvery,
    monthlyEachDates,
    monthlyOnTheDayOption,
    monthlyOnTheFirstLastOption,
    reportingDateDays,
    reportingDateBeforeAfter,
    timeGenerated,
    weeklyEvery,
    weeklyOn,
    reportingDates,
  }));

  // if (autoSync) {
  //   const timeZone = defaultTimeZone || '+00:00';

  //   const hour = Number(moment(`${timeGenerated} ${amPmGenerated}`, 'h A').format('H'));
  //   const startMoment = moment(syncStartOn).zone(timeZone).set('hour', hour).set('minute', 0).set('second', 0);
  //   const currentMoment = moment.utc().zone(timeZone);
  //   const currentHour = Number(currentMoment.format('H'));
  //   const offset = currentHour >= hour ? 1 : 0;

  //   switch (frequency) {
  //     case 'DAILY':
  //       switch (true) {
  //         case startMoment.isSameOrBefore(currentMoment, 'day'):
  //           nextMoment = moment(currentMoment).set('hour', hour).set('minute', 0).set('second', 0);

  //           if (currentHour >= hour) {
  //             nextMoment = moment(nextMoment).add(1, 'day');
  //           }

  //           break;
  //         case startMoment.isAfter(currentMoment, 'day'):
  //           nextMoment = moment(startMoment);
  //           break;
  //         default:
  //           break;
  //       }

  //       autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)}<br/>${capitalize(frequency)}`;
  //       break;
  //     case 'WEEKLY':
  //       const startSundayMoment = moment(startMoment).startOf('isoWeek').day(0);
  //       const currentWeekDay = Number(currentMoment.format('e'));
  //       const currentSundayMoment = moment(currentMoment).startOf('isoWeek').day(0);
  //       const weeksFromStart = moment.duration(currentSundayMoment.diff(startSundayMoment)).asWeeks();
  //       const unavailableWeekDays = Array.from(Array(7).keys()).slice(0, currentWeekDay + offset);
  //       const availableWeekDays = difference(weeklyOn, unavailableWeekDays);
  //       const [nextAvailableWeekDay] = availableWeekDays.length ? availableWeekDays : weeklyOn;
  //       nextMoment = moment(currentSundayMoment)
  //         .add(weeksFromStart % weeklyEvery, 'week')
  //         .day(nextAvailableWeekDay)
  //         .set('hour', hour)
  //         .set('minute', 0)
  //         .set('second', 0);

  //       if (nextMoment.isSameOrBefore(currentMoment)) {
  //         nextMoment.add(weeklyEvery, 'week');
  //       }

  //       autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)}<br/>${capitalize(frequency)} every ${weeklyEvery} week${
  //         weeklyEvery > 1 ? 's' : ''
  //       } on ${Array.from(weeklyOn)
  //         .sort()
  //         .map((weekday) => moment().weekday(weekday).format('dddd'))
  //         .join(', ')}`;

  //       break;
  //     case 'MONTHLY':
  //       const startMonthMoment = moment(startMoment).startOf('month');
  //       const currentMonthMoment = moment(currentMoment).startOf('month');
  //       const currentMonthDay = Number(currentMoment.format('D'));
  //       const monthsFromStart = moment.duration(currentMonthMoment.diff(startMonthMoment)).asMonths();
  //       nextMoment = moment(currentMonthMoment)
  //         .add(monthsFromStart % monthlyEvery, 'month')
  //         .set('hour', hour)
  //         .set('minute', 0)
  //         .set('second', 0);
  //       const nextMonth = moment(nextMoment).startOf('month');

  //       switch (monthlyMode) {
  //         case 'EACH':
  //           const unavailableMonthDays = Array.from(Array(32).keys()).slice(1, currentMonthDay + offset);
  //           const availableMonthDays = difference(monthlyEachDates, unavailableMonthDays);
  //           let [nextAvailableMonthDay] = availableMonthDays.length ? availableMonthDays : monthlyEachDates;

  //           if (nextMoment.isBefore(currentMonthMoment)) {
  //             nextMoment.add(monthlyEvery, 'month');
  //           } else if (nextMoment.isAfter(currentMonthMoment)) {
  //             [nextAvailableMonthDay] = monthlyEachDates;
  //           }

  //           nextMoment = nextMoment.date(nextAvailableMonthDay);

  //           if (nextMoment.isSameOrBefore(currentMoment)) {
  //             nextMoment.add(monthlyEvery, 'month');
  //           }

  //           autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)}<br/>${capitalize(
  //             frequency,
  //           )} every ${monthlyEvery} month${monthlyEvery > 1 ? 's' : ''} on ${Array.from(monthlyEachDates)
  //             .sort()
  //             .map((monthDay) => moment().date(monthDay).format('Do'))
  //             .join(', ')} day`;
  //           break;
  //         case 'ON_THE':
  //           switch (monthlyOnTheFirstLastOption) {
  //             case 'FIRST':
  //               if (monthlyOnTheDayOption === 'DAY') {
  //                 nextMoment = nextMoment.startOf('month');
  //               } else {
  //                 nextMoment = moment(nextMonth).day(capitalize(monthlyOnTheDayOption));

  //                 if (nextMoment.isBefore(nextMonth)) {
  //                   nextMoment.add(1, 'week');
  //                 }
  //               }
  //               break;
  //             case 'LAST':
  //               if (monthlyOnTheDayOption === 'DAY') {
  //                 nextMoment = nextMoment.endOf('month');
  //               } else {
  //                 nextMoment = moment(nextMonth).endOf('month').day(capitalize(monthlyOnTheDayOption));

  //                 if (nextMoment.isAfter(nextMonth)) {
  //                   nextMoment.subtract(1, 'week');
  //                 }
  //               }
  //               break;
  //             default:
  //               break;
  //           }

  //           autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)}<br/>${capitalize(
  //             frequency,
  //           )} every ${monthlyEvery} month${monthlyEvery > 1 ? 's' : ''} on the ${toLower(monthlyOnTheFirstLastOption)} ${capitalize(
  //             monthlyOnTheDayOption,
  //           )}`;
  //           break;
  //         default:
  //           break;
  //       }

  //       break;
  //     case 'REPORTING_DATE':
  //       const reportDates = reportingDates.split(', ').sort();
  //       let expectMoment, nextDate;

  //       switch (reportingDateBeforeAfter) {
  //         case 'BEFORE':
  //           expectMoment = currentMoment.add(reportingDateDays, 'day');
  //           nextDate = reportDates.find((date) => moment(date).isAfter(expectMoment));
  //           nextMoment = moment(nextDate).set('hour', hour).set('minute', 0).set('second', 0);
  //           autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)} <br/>Reporting Date`;
  //           break;
  //         case 'AFTER':
  //           expectMoment = currentMoment.subtract(reportingDateDays, 'day');
  //           nextDate = reportDates.find((date) => moment(date).isAfter(expectMoment));
  //           nextMoment = moment(nextDate).set('hour', hour).set('minute', 0).set('second', 0);
  //           autoSyncInfo = `${autoSyncInfo}${nextMoment.format(momentConfig.long)} <br/>Reporting Date`;
  //           break;
  //         default:
  //           break;
  //       }

  //       break;
  //     default:
  //       break;
  //   }
  // }

  return (
    <div className="app-header">
      <ul {...ui([uiA`m-right-40`, 'pd-status'])}>
        <li data-tip={newerUpdatesLabel} data-place="bottom" data-offset="{'top':15}" style={{ fontSize: '15px' }}>
          {newerUpdatesAvailable && <FontAwesomeIcon aria-hidden="true" icon={['fal', 'exclamation-triangle']} color="orange" />}
          <span style={{ lineHeight: '20px', fontSize: '11px' }}>
            <strong>Last Published:</strong>
            {lastPublishDate}
          </span>
        </li>
        {!!autoSync && (
          <li>
            <button {...ui([uiButton`--clean --small`])} data-tip data-for="next-sync-id" onClick={onSync}>
              <div {...ui([uiButton`__content`, uiA`f text-align-left font-size-13 p-x-small color-nobel:${!autoSync}`])} tabIndex={-1}>
                <div {...ui([uiIconSvg`#${autosyncStatus.on.iconModifier}`, uiA`m-right-3`])} />
                <span {...ui([uiA`font-size-11 top-1- m-left-none m-right-small`])}>
                  <span {...ui([uiA`semibold m-left-none top-0`])}>Next Sync:&nbsp;</span>
                  {moment().diff(nextMoment) > 604800000 ? nextMoment.format(momentConfig.short) : nextMoment.fromNow()}
                </span>
                <ReactTooltip id="next-sync-id" delayShow={500}>
                  <span {...ui([uiA`text-align-left m-left-none`])}>
                    {autosyncStatus.on.tooltip}
                    <br />
                    <FroalaEditorView
                      model={`Next run: ${nextMoment?.format('lll')}<br/>${startCase(camelCase(frequency))}${
                        humanizedString ? ` ${humanizedString}` : ''
                      }`}
                    />
                  </span>
                </ReactTooltip>
              </div>
            </button>
          </li>
        )}
        {withError ? (
          <li style={{ fontSize: '15px' }} data-tip="Sync failed" data-place="bottom" data-offset="{'top':15}">
            <FontAwesomeIcon icon={['fal', 'sync']} color="red" />
            <span data-tip={lastSyncLabel} data-place="bottom" data-offset="{'top':15}" style={{ lineHeight: '20px', fontSize: '11px' }}>
              <strong>Last Sync: </strong>
              {moment(createdAt).format('MMM D, YYYY, h:mm:ss a')}
            </span>
          </li>
        ) : (
          <li
            style={{ fontSize: '15px' }}
            data-tip={!appliedAt && moment(createdAt).diff(moment(), 'days') >= 1 ? 'Synced data has not been applied.' : ''}
            data-place="bottom"
            data-offset="{'top':15}"
          >
            {!appliedAt && moment(createdAt).diff(moment(), 'days') >= 1 ? <FontAwesomeIcon icon={['fal', 'sync']} color="orange" /> : ''}
            {!!syncStatus && <FontAwesomeIcon icon={['fal', 'sync']} spin />}
            <span data-tip={lastSyncLabel} data-place="bottom" data-offset="{'top':15}" style={{ lineHeight: '20px', fontSize: '11px' }}>
              <strong>Last Sync: </strong>
              {syncStatus ? 'Sync in Progress' : moment(createdAt).format('MMM D, YYYY, h:mm:ss a')}
            </span>
          </li>
        )}
      </ul>
      <ReactTooltip />
      <Link to="/home/edit" className="hollow-btn header-title" href="homepage">
        <h1 className="header-title__title">
          <FontAwesomeIcon icon={['fal', 'browser']} />
          <span>Public Dashboard</span>
        </h1>
        <span className="header-title__divider">|</span>
        <span className="header-title__subtitle">{planName}</span>
      </Link>
      {pathname !== '/sync' && (
        <Dropdown
          defaultValue={<FontAwesomeIcon icon={['fal', 'sliders-h']} id="systemSettings" />}
          customClass={uiMerge([uiA`w-25 border-left-panel`, 'system-setting-btn'])}
        >
          <button type="button" className="hollow-btn" onClick={onToggleGeneralSettings}>
            <FontAwesomeIcon icon={['fal', 'cog']} />
            <span>Settings</span>
          </button>
          <button type="button" className="hollow-btn" disabled={pathname === '/sync'} onClick={onTogglePreviewPublishSettings}>
            <FontAwesomeIcon icon={['fal', 'globe-americas']} />
            <span>Preview/Publish</span>
          </button>
          <button type="button" className={`hollow-btn${syncClass}`} disabled={pathname === '/sync'} onClick={onSync}>
            <FontAwesomeIcon icon={['fal', 'sync']} />
            <span>{syncButtonLabel}</span>
          </button>
        </Dropdown>
      )}
    </div>
  );
}
